import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, of, switchMap, tap } from 'rxjs';
import { CampaignService } from '../../services/campaign.service';
import { NotificationService } from '../../services/notification.service';
import { CampaignsApiActions, CampaignsMutationActions, CampaignsPageActions } from './campaigns.actions';

@Injectable()
export class CampaignsEffects {
  private readonly actions$ = inject(Actions);
  private readonly campaignService = inject(CampaignService);
  private readonly notificationService = inject(NotificationService);

  readonly loadCampaigns$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CampaignsPageActions.opened, CampaignsPageActions.reloaded),
      switchMap(() => this.campaignService.getAll().pipe(
        map((campaigns) => CampaignsApiActions.loadSucceeded({ campaigns })),
        catchError((error: { message?: string }) => of(
          CampaignsApiActions.loadFailed({ error: error?.message ?? 'Unable to load campaigns' }),
        )),
      )),
    ),
  );

  readonly notifyLoadFailure$ = createEffect(
    () => this.actions$.pipe(
      ofType(CampaignsApiActions.loadFailed),
      tap(() => this.notificationService.error('CAMPAIGNS.ERROR_LOADING')),
    ),
    { dispatch: false },
  );

  readonly setFeatured$ = createEffect(() => this.actions$.pipe(
    ofType(CampaignsMutationActions.setFeaturedRequested),
    concatMap(({ requestId, id }) => this.campaignService.setFeatured(id).pipe(
      map(() => CampaignsMutationActions.setFeaturedSucceeded({ requestId, id })),
      catchError((error: { message?: string }) => of(CampaignsMutationActions.setFeaturedFailed({
        requestId,
        error: error?.message ?? 'Unable to set featured campaign',
      }))),
    )),
  ));

  readonly setStatus$ = createEffect(() => this.actions$.pipe(
    ofType(CampaignsMutationActions.setStatusRequested),
    concatMap(({ requestId, id, isActive }) => this.campaignService.setStatus(id, isActive).pipe(
      map(() => CampaignsMutationActions.setStatusSucceeded({ requestId, id, isActive })),
      catchError((error: { message?: string }) => of(CampaignsMutationActions.setStatusFailed({
        requestId,
        error: error?.message ?? 'Unable to update campaign status',
      }))),
    )),
  ));

  readonly setPopup$ = createEffect(() => this.actions$.pipe(
    ofType(CampaignsMutationActions.setPopupRequested),
    concatMap(({ requestId, id, isPopup }) => this.campaignService.setPopup(id, isPopup).pipe(
      map(() => CampaignsMutationActions.setPopupSucceeded({ requestId, id, isPopup })),
      catchError((error: { message?: string }) => of(CampaignsMutationActions.setPopupFailed({
        requestId,
        error: error?.message ?? 'Unable to update campaign popup',
      }))),
    )),
  ));

  readonly createCampaign$ = createEffect(() => this.actions$.pipe(
    ofType(CampaignsMutationActions.createRequested),
    concatMap(({ requestId, request }) => this.campaignService.create(request).pipe(
      map((campaign) => CampaignsMutationActions.createSucceeded({ requestId, campaign })),
      catchError((error: { message?: string }) => of(CampaignsMutationActions.createFailed({
        requestId,
        error: error?.message ?? 'Unable to create campaign',
      }))),
    )),
  ));

  readonly updateCampaign$ = createEffect(() => this.actions$.pipe(
    ofType(CampaignsMutationActions.updateRequested),
    concatMap(({ requestId, id, request }) => this.campaignService.update(id, request).pipe(
      map(() => CampaignsMutationActions.updateSucceeded({ requestId, id })),
      catchError((error: { message?: string }) => of(CampaignsMutationActions.updateFailed({
        requestId,
        error: error?.message ?? 'Unable to update campaign',
      }))),
    )),
  ));
}